import { Controller } from 'stimulus'
import { parseUnit } from 'src/utils/units_format'

const DEFAULT_RENDER_OPTIONS = Object.freeze({
  viewMode: 'default',
  shapeOnly: false,
})
const LOAD_FIELD_SELECTOR = [
  '[data-v2-load-field]',
  '[data-v2-load-extra-field]',
  '#quote_commodity_description',
  '#quote_commodity_length',
  '#quote_commodity_support_length',
  '#quote_commodity_support_offset',
  '#quote_commodity_width',
  '#quote_commodity_height',
  '#quote_commodity_weight',
].join(', ')
const EXTRA_LOAD_ROW_SELECTOR = '[data-behavior~="v2-load-dimensions-extra-row"]'
const SELECTED_LOAD_ASSIGNMENT_SELECTOR = '[data-behavior~="v2-selected-load-assignment-id"]'
const LOAD_LIST_TOGGLE_SELECTOR = '[data-behavior~="v2-load-list-toggle"]'

const LIVE_PREVIEW_DEBOUNCE_MS = 350

function stringValue(value) {
  return value === null || value === undefined ? '' : String(value)
}

function humanizeFieldName(field) {
  return stringValue(field).replace(/_/g, ' ').trim()
}

// Pure, exported for unit testing. Turns a backend render_reason code into a
// short human phrase for the readiness strip and live placeholder.
export function humanizeRenderReason(reason) {
  const code = stringValue(reason).trim()
  if (!code) return ''

  switch (code) {
    case 'invalid_trailer_deck_height':
      return 'needs deck height'
    case 'invalid_trailer_geometry':
      return 'check trailer geometry values'
    case 'invalid_trailer_length':
      return 'needs trailer length'
    default:
      break
  }

  if (code.indexOf('unsupported') === 0) return "this type can't be rendered yet"

  return code.replace(/_/g, ' ')
}

// Pull the human message out of a structured render_reason_detail. Each detail
// is a string-keyed hash { field, label, message } produced by the builder.
function detailMessage(detail) {
  return detail && typeof detail === 'object' ? stringValue(detail.message).trim() : ''
}

// Pure, exported for unit testing. Derives the readiness strip model from a
// render scene (+ optional paused reason / structured paused details). Keys off
// missing_geometry_fields, the spec-verified string array on
// render_scene.source_confidence. When pausedDetails carries structured
// messages they win over the coarse humanized reason; shape:
//   deriveReadiness(scene, { pausedReason: 'invalid_trailer_geometry',
//                            pausedDetails: [{ field, label, message }] })
export function deriveReadiness(scene, options = {}) {
  const sourceConfidence = (scene && scene.source_confidence) || {}
  const serverReadiness = options.readiness && typeof options.readiness === 'object'
    ? options.readiness
    : null
  const missingFields = serverReadiness && Array.isArray(serverReadiness.missing_fields)
    ? serverReadiness.missing_fields
    : Array.isArray(sourceConfidence.missing_geometry_fields)
      ? sourceConfidence.missing_geometry_fields
      : []
  const spacingsMissing = missingFields.indexOf('chassis_spacings') !== -1

  const chips = [{ kind: 'ready', label: 'Renders' }]
  if (serverReadiness) {
    chips.push(serverReadiness.render_exact === true
      ? { kind: 'ready', label: '3D geometry exact' }
      : { kind: 'assumed', label: '3D geometry uses defaults' })
  }
  missingFields.forEach(field => {
    if (field === 'chassis_spacings') {
      // Covers both the per-axle default (5') and the inter-unit default (8'),
      // so the chip names the fact without pinning a single number.
      chips.push({ kind: 'assumed', label: 'Axle spacings assumed' })
    } else if (field === 'unit_empty_weights') {
      chips.push({ kind: 'assumed', label: 'Unit empty weights missing' })
    } else if (field === 'axle_tire_configuration') {
      chips.push({ kind: 'assumed', label: 'Axle tire data missing' })
    } else if (field === 'planning_topology') {
      chips.push({ kind: 'assumed', label: 'Planning topology unsupported' })
    } else {
      chips.push({ kind: 'assumed', label: `${humanizeFieldName(field)} assumed` })
    }
  })

  let planning
  if (serverReadiness && serverReadiness.load_plannable !== true) {
    planning = {
      kind: 'warning',
      label: 'Load planning unavailable — complete axle positions and equipment data.',
    }
  } else if ((serverReadiness && serverReadiness.load_exact !== true) || spacingsMissing) {
    planning = {
      kind: 'warning',
      label: 'Load planning uses estimates — complete the assumed equipment fields.',
    }
  } else {
    planning = { kind: 'ready', label: 'Planner geometry complete — per-axle load calculation available.' }
  }

  const pausedDetails = Array.isArray(options.pausedDetails) ? options.pausedDetails : []
  const pausedDetailMessages = pausedDetails.map(detailMessage).filter(message => message.length > 0)
  const pausedReason = stringValue(options.pausedReason).trim()

  let paused = null
  if (pausedDetailMessages.length > 0) {
    paused = { label: `Preview paused — ${pausedDetailMessages.slice(0, 3).join(' ')}` }
  } else if (pausedReason) {
    paused = { label: `Preview paused — ${humanizeRenderReason(pausedReason)}` }
  }

  return { chips, planning, paused }
}

export function parseLiveDimensionInches(rawValue, key, options = {}) {
  const value = stringValue(rawValue).trim()
  if (!value) return null

  if (
    typeof window !== 'undefined' &&
    typeof window.parseDimensionInputInches !== 'function' &&
    typeof window.ensureDimensionInputParsing === 'function'
  ) {
    window.ensureDimensionInputParsing()
  }

  if (typeof window !== 'undefined' && typeof window.parseDimensionInputInches === 'function') {
    const inches = Number(window.parseDimensionInputInches(value, key))
    return Number.isFinite(inches) && (inches > 0 || (options.allowZero && inches === 0)) ? inches : null
  }

  const feet = parseUnit(value)
  return Number.isFinite(feet) && (feet > 0 || (options.allowZero && feet === 0)) ? feet * 12 : null
}

export function parseLiveWeightPounds(rawValue) {
  const value = stringValue(rawValue).trim()
  if (!value) return null

  if (
    typeof window !== 'undefined' &&
    typeof window.parseWeightInputLb !== 'function' &&
    typeof window.ensureWeightInputParsing === 'function'
  ) {
    window.ensureWeightInputParsing()
  }

  if (typeof window !== 'undefined' && typeof window.parseWeightInputLb === 'function') {
    const pounds = Number(window.parseWeightInputLb(value))
    return Number.isFinite(pounds) && pounds > 0 ? pounds : null
  }

  const normalizedValue = value.toLowerCase()
  const multiplier = normalizedValue.match(/(kg|kilo|kilgram|kilogramm|killogramm|kilogram)/)
    ? 2.2046226218
    : normalizedValue.match(/(tons?|tonnes?|metric\s*tons?|mt\b|\bt\b)/)
      ? normalizedValue.match(/tonnes?|metric\s*tons?|mt\b|\bt\b/) ? 2204.6226218 : 2000
      : 1
  const numeric = Number(normalizedValue.replace(/[^\d.]/g, ''))
  return Number.isFinite(numeric) && numeric > 0 ? Math.round(numeric * multiplier) : null
}

export default class extends Controller {
  static targets = ['payload', 'container', 'stretchControl', 'stretchMeta', 'loadRow', 'loadListToggle', 'status']

  connect() {
    this.connected = true
    if (this.loadPreview) {
      this.connectLoadPreview()
      return
    }

    this.currentStretchMode = 'closed'
    this.hasRenderedScene = false
    this.livePreviewSequence = 0
    this.highlightedFieldGroups = []
    this.bindFormStretchControls()
    this.bindRenderStretchControls()
    this.mount()
    this.connectLivePreview()
  }

  disconnect() {
    this.connected = false
    if (this.loadPreview) {
      this.disconnectLoadPreview()
      return
    }

    this.disconnectLivePreview()
    this.unbindRenderStretchControls()
    this.unbindFormStretchControls()
    this.destroyPanel()
    this.trailer3d = null
  }

  async mount() {
    const payload = this.renderPayload

    if (!payload) {
      this.showInitialPlaceholder('render_payload_missing')
      return
    }

    if (payload.renderable !== true || !payload.render_scene) {
      this.showInitialPlaceholder(payload.render_reason || 'render_unavailable')
      return
    }

    try {
      const trailer3d = await import(
        /* webpackChunkName: "tractor-trailer-3d" */ 'src/tractor_trailer_3d'
      )
      if (!this.connected) return

      this.trailer3d = trailer3d
      // A fast typist can trigger the live path before this initial import
      // resolves; reuse any panel it already created rather than leaking one.
      if (!this.panel) {
        this.panel = trailer3d.createPanel(this.containerTarget, {
          showStretchControls: false,
          stretchControlsEnabled: this.stretchControlsActive,
        })
      }
      const renderOverrides = payload.render_overrides || payload.renderOverrides || null
      this.panel.update({
        trailer: payload.render_scene,
        stretchMode: 'closed',
        renderOptions: DEFAULT_RENDER_OPTIONS,
        renderOverrides,
        allowDefaultLegalEnvelope: this.allowDefaultLegalEnvelope,
      })
      this.hasRenderedScene = true
      this.syncStretchControlsWithForm()
      if (this.livePreviewEnabled) {
        this.lastReadiness = payload.readiness || null
        this.renderReadiness(payload.render_scene, { readiness: this.lastReadiness })
      }
    } catch (error) {
      this.showInitialPlaceholder(error instanceof Error ? error.message : 'render_mount_failed')
    }
  }

  // In live mode the initial not-yet-computed state should read as an
  // invitation rather than an internal reason code.
  showInitialPlaceholder(reason) {
    if (this.livePreviewEnabled) {
      this.showPlaceholder(
        'Renders as you type — choose a trailer type and enter the key dimensions.',
        '3D preview'
      )
      return
    }

    this.showPlaceholder(reason)
  }

  destroyPanel() {
    if (!this.panel) return

    this.panel.destroy()
    this.panel = null
  }

  // Toggles the paused class on the mount so the last rendered frame is visibly
  // dimmed while the current inputs are not renderable.
  setPaused(paused) {
    this.element.classList.toggle('ttRenderMount--paused', paused === true)
  }

  showPlaceholder(reason, titleText = '3D preview unavailable') {
    this.destroyPanel()
    this.syncStretchControlDisplay(false)

    const wrapper = document.createElement('div')
    wrapper.className = 'ttRenderFallback'

    const body = document.createElement('div')
    body.className = 'ttRenderFallbackBody'

    const title = document.createElement('strong')
    title.textContent = titleText

    const detail = document.createElement('div')
    detail.textContent = String(reason || 'render_unavailable')

    body.appendChild(title)
    body.appendChild(detail)
    wrapper.appendChild(body)

    this.containerTarget.replaceChildren(wrapper)
  }

  connectLivePreview() {
    if (!this.livePreviewEnabled) return

    this.liveFormElement = this.element.closest('form')
    if (!this.liveFormElement) return

    this.handleLiveInput = () => {
      window.clearTimeout(this.liveDebounceTimer)
      this.liveDebounceTimer = window.setTimeout(() => this.requestLivePreview(), LIVE_PREVIEW_DEBOUNCE_MS)
    }
    this.handleLiveChange = event => {
      // A trailer type switch invalidates the current render entirely — a stale
      // wrong-type frame must never persist across it. Clear immediately; the
      // fetch below re-renders once the new type's geometry is valid.
      if (this.isUnitTypeChange(event)) this.clearLiveRenderForTypeSwitch()

      window.clearTimeout(this.liveDebounceTimer)
      // Document-level delegated handlers (axle-spacing row sync, geometry
      // toggles) run after this form-level listener; defer one tick so the
      // FormData snapshot sees the updated rows instead of the previous state.
      this.liveDebounceTimer = window.setTimeout(() => this.requestLivePreview(), 0)
    }

    this.liveFormElement.addEventListener('input', this.handleLiveInput)
    this.liveFormElement.addEventListener('change', this.handleLiveChange)
  }

  isUnitTypeChange(event) {
    const target = event && event.target
    return !!(
      target &&
      (target.name === 'tractor_trailer[unit_type]' || target.name === 'units[][unit_type]')
    )
  }

  clearLiveRenderForTypeSwitch() {
    this.destroyPanel()
    this.hasRenderedScene = false
    this.liveScene = null
    this.lastRenderedScene = null
    this.setPaused(false)
    this.clearFieldHighlights()
    this.clearReadiness()
    this.syncStretchControlsWithForm()
    this.showInitialPlaceholder('unit_type_changed')
  }

  // The readiness strip describes a rendered scene; across a type switch that
  // scene no longer exists, so stale "Renders / Load planning ready" chips
  // must not outlive the canvas they described.
  clearReadiness() {
    if (this.hasStatusTarget) this.statusTarget.replaceChildren()
  }

  disconnectLivePreview() {
    window.clearTimeout(this.liveDebounceTimer)
    if (this.liveAbortController) this.liveAbortController.abort()
    this.clearFieldHighlights()

    if (this.liveFormElement) {
      if (this.handleLiveInput) this.liveFormElement.removeEventListener('input', this.handleLiveInput)
      if (this.handleLiveChange) this.liveFormElement.removeEventListener('change', this.handleLiveChange)
    }

    this.liveFormElement = null
    this.handleLiveInput = null
    this.handleLiveChange = null
    this.liveAbortController = null
  }

  async requestLivePreview() {
    if (!(this.livePreviewEnabled && this.liveFormElement)) return

    if (this.liveAbortController) this.liveAbortController.abort()
    const abortController = new AbortController()
    this.liveAbortController = abortController
    const sequence = (this.livePreviewSequence += 1)
    const body = new FormData(this.liveFormElement)
    // The preview endpoint is always POST. Edit forms carry Rails' hidden
    // `_method=put`, which would otherwise be interpreted by Rack before the
    // request reaches the preview action.
    body.delete('_method')

    let response
    try {
      response = await fetch(this.livePreviewUrl, {
        method: 'POST',
        body,
        credentials: 'same-origin',
        signal: abortController.signal,
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': this.csrfToken(),
        },
      })
    } catch (error) {
      if (error && error.name === 'AbortError') return
      this.handleLiveFailure(sequence)
      return
    }

    if (sequence !== this.livePreviewSequence || !this.connected) return

    if (!response.ok) {
      this.handleLiveFailure(sequence)
      return
    }

    let payload
    try {
      payload = await response.json()
    } catch (_error) {
      this.handleLiveFailure(sequence)
      return
    }

    if (sequence !== this.livePreviewSequence || !this.connected) return

    this.applyLiveResponse(payload, sequence)
  }

  async applyLiveResponse(payload, sequence) {
    const renderable = payload && payload.renderable === true
    const scene = payload && payload.render_scene
    const details = payload && Array.isArray(payload.render_reason_details)
      ? payload.render_reason_details
      : []

    // Every live response reconciles the field highlights: clear whatever was
    // marked last time, then re-mark only the currently offending inputs.
    this.clearFieldHighlights()

    if (renderable && scene) {
      const panel = await this.ensurePanel()
      if (!panel || !this.connected) return
      // The panel import above can suspend long enough for a newer response to
      // land; a stale scene must never overwrite it after the await.
      if (sequence !== undefined && sequence !== this.livePreviewSequence) return

      this.liveScene = scene
      panel.update({
        trailer: scene,
        stretchMode: this.currentStretchMode,
        renderOptions: DEFAULT_RENDER_OPTIONS,
        allowDefaultLegalEnvelope: this.allowDefaultLegalEnvelope,
      })
      this.hasRenderedScene = true
      this.setPaused(false)
      this.lastReadiness = payload.readiness || null
      this.renderReadiness(scene, { readiness: this.lastReadiness })
      this.syncStretchControlsWithForm()
      return
    }

    this.applyFieldHighlights(details)

    // Never destroy a scene that already rendered — pause instead. Dim the
    // stale frame so it visibly reads as not-current.
    if (this.hasRenderedScene) {
      this.setPaused(true)
      this.renderReadiness(this.lastRenderedScene, {
        readiness: this.lastReadiness,
        pausedReason: (payload && payload.render_reason) || 'render_unavailable',
        pausedDetails: details,
      })
      return
    }

    const reason = payload && payload.render_reason
    this.showPlaceholder(
      this.notRenderingYetMessage(reason, details) ||
        'Renders as you type — choose a trailer type and enter the key dimensions.',
      '3D preview'
    )
  }

  // Pre-first-render placeholder copy. Prefers the first structured detail
  // message (with a "(+N more)" tail when several fields are wrong); falls back
  // to the coarse humanized reason for the invalid_* codes. Returns '' when
  // there's nothing actionable to say (caller shows the neutral invitation).
  notRenderingYetMessage(reason, details) {
    const messages = (details || [])
      .map(detail => (detail && typeof detail === 'object' ? stringValue(detail.message).trim() : ''))
      .filter(message => message.length > 0)

    if (messages.length > 0) {
      const extra = messages.length - 1
      const suffix = extra > 0 ? ` (+${extra} more)` : ''
      return `Not rendering yet — ${messages[0]}${suffix}`
    }

    const code = stringValue(reason)
    if (code.indexOf('invalid_') === 0) return `Not rendering yet — ${humanizeRenderReason(code)}.`

    return ''
  }

  // Bootstrap 3 has-warning on the .form-group of each offending simplified
  // input. Silently skips fields absent from the DOM (e.g. hidden twin fields),
  // and records the groups it touched so the next response can clear them.
  applyFieldHighlights(details) {
    if (!this.liveFormElement || !Array.isArray(details)) return

    this.highlightedFieldGroups = this.highlightedFieldGroups || []

    details.forEach(detail => {
      const field = detail && typeof detail === 'object' ? stringValue(detail.field).trim() : ''
      if (!field) return

      const simplifiedName = `tractor_trailer[${field}]`
      const detailedField = field === 'trailer_length' ? 'length' : field
      const detailedName = `units[][${detailedField}]`
      const formElements = Array.from(this.liveFormElement.elements)
      const input = formElements.find(element => element.name === simplifiedName) ||
        formElements.find(element => (
          element.name === detailedName &&
          element.closest &&
          element.closest('[data-unit-kind="trailer"]')
        )) ||
        formElements.find(element => element.name === detailedName)
      const group = input && input.closest ? input.closest('.form-group') : null
      if (!group) return

      group.classList.add('has-warning')
      if (this.highlightedFieldGroups.indexOf(group) === -1) this.highlightedFieldGroups.push(group)
    })
  }

  clearFieldHighlights() {
    if (!this.highlightedFieldGroups) return

    this.highlightedFieldGroups.forEach(group => group.classList.remove('has-warning'))
    this.highlightedFieldGroups = []
  }

  handleLiveFailure(sequence) {
    if (sequence !== this.livePreviewSequence) return

    if (this.hasRenderedScene) {
      this.showReadinessMessage('Preview unavailable — retrying on next change', 'warning')
    }
  }

  async ensurePanel() {
    if (this.panel) return this.panel

    try {
      const trailer3d = this.trailer3d || (await import(
        /* webpackChunkName: "tractor-trailer-3d" */ 'src/tractor_trailer_3d'
      ))
      if (!this.connected) return null

      this.trailer3d = trailer3d
      this.panel = trailer3d.createPanel(this.containerTarget, {
        showStretchControls: false,
        stretchControlsEnabled: this.stretchControlsActive,
      })
      return this.panel
    } catch (_error) {
      return null
    }
  }

  renderReadiness(scene, options = {}) {
    if (!this.hasStatusTarget) return

    this.lastRenderedScene = scene || this.lastRenderedScene
    const model = deriveReadiness(this.lastRenderedScene, options)

    const chipsRow = document.createElement('div')
    chipsRow.className = 'ttRenderReadinessChips'
    model.chips.forEach(chip => {
      const el = document.createElement('span')
      el.className = `ttRenderReadinessChip ttRenderReadinessChip--${chip.kind}`
      el.textContent = chip.label
      chipsRow.appendChild(el)
    })

    const planningLine = document.createElement('div')
    planningLine.className = `ttRenderReadinessLine ttRenderReadinessLine--${model.planning.kind}`
    planningLine.textContent = model.planning.label

    const fragment = document.createDocumentFragment()
    fragment.appendChild(chipsRow)
    fragment.appendChild(planningLine)

    if (model.paused) {
      const pausedLine = document.createElement('div')
      pausedLine.className = 'ttRenderReadinessLine ttRenderReadinessLine--paused'
      pausedLine.textContent = model.paused.label
      fragment.appendChild(pausedLine)
    }

    this.statusTarget.replaceChildren(fragment)
  }

  showReadinessMessage(message, kind = 'warning') {
    if (!this.hasStatusTarget) return

    const line = document.createElement('div')
    line.className = `ttRenderReadinessLine ttRenderReadinessLine--${kind === 'ready' ? 'ready' : 'paused'}`
    line.textContent = message

    // Keep any existing chips/planning line; only replace a prior message line.
    const existingMessage = this.statusTarget.querySelector('[data-live-message]')
    if (existingMessage) existingMessage.remove()
    line.setAttribute('data-live-message', 'true')
    this.statusTarget.appendChild(line)
  }

  csrfToken() {
    const tag = document.querySelector('meta[name="csrf-token"]')
    return tag ? tag.getAttribute('content') : ''
  }

  connectLoadPreview() {
    this.selectedLoadIndex = 0
    this.renderTimer = null
    this.loadPreviewState = 'pending'
    this.loadPreviewError = null
    this.loadListExpanded = false
    this.formElement = this.element.closest('form')
    this.loadInputRoot = this.formElement || this.element
    this.editableLoadRows = this.normalizeLoadRows(this.initialLoadRows)
    this.editorHydrating = false
    this.handleLoadFieldChange = this.handleLoadFieldChange.bind(this)
    this.handleLoadSelectionClick = this.handleLoadSelectionClick.bind(this)

    this.loadInputRoot.addEventListener('input', this.handleLoadFieldChange)
    this.loadInputRoot.addEventListener('keyup', this.handleLoadFieldChange)
    this.loadInputRoot.addEventListener('change', this.handleLoadFieldChange)
    this.loadInputRoot.addEventListener('click', this.handleLoadSelectionClick)

    this.observeExtraLoadRows()
    this.hydrateSelectedLoadEditor()
    this.mountLoadPreview()
    this.renderLoadPreview()
  }

  disconnectLoadPreview() {
    if (this.loadInputRoot) {
      this.loadInputRoot.removeEventListener('input', this.handleLoadFieldChange)
      this.loadInputRoot.removeEventListener('keyup', this.handleLoadFieldChange)
      this.loadInputRoot.removeEventListener('change', this.handleLoadFieldChange)
      this.loadInputRoot.removeEventListener('click', this.handleLoadSelectionClick)
    }
    if (this.extraLoadRowsObserver) this.extraLoadRowsObserver.disconnect()
    if (this.renderTimer) window.cancelAnimationFrame(this.renderTimer)

    this.destroyPanel()
    this.formElement = null
    this.loadInputRoot = null
    this.trailer3d = null
  }

  async mountLoadPreview() {
    try {
      const trailer3d = await import(
        /* webpackChunkName: "tractor-trailer-3d" */ 'src/tractor_trailer_3d'
      )
      if (!this.connected) return

      this.trailer3d = trailer3d
      this.panel = trailer3d.createPanel(this.containerTarget, {
        loadOnly: true,
        showStretchControls: false,
      })
      this.loadPreviewState = 'ready'
      this.renderLoadPreview()
    } catch (error) {
      this.loadPreviewState = 'unavailable'
      this.loadPreviewError = error instanceof Error ? error.message : 'render_mount_failed'
      this.showPlaceholder(this.loadPreviewError)
    }
  }

  observeExtraLoadRows() {
    if (typeof window === 'undefined' || !window.MutationObserver) return

    const container = this.extraLoadRowsContainer()
    if (!container) return

    this.extraLoadRowsObserver = new MutationObserver(() => this.scheduleLoadRender())
    this.extraLoadRowsObserver.observe(container, { childList: true, subtree: true })
  }

  handleLoadFieldChange(event) {
    const target = event.target
    if (!(target && target.matches && target.matches(LOAD_FIELD_SELECTOR))) return
    if (this.editorHydrating) return

    this.commitSelectedLoadEditor()
    this.scheduleLoadRender()
  }

  handleLoadSelectionClick(event) {
    const toggle = event.target && event.target.closest
      ? event.target.closest(LOAD_LIST_TOGGLE_SELECTOR)
      : null
    if (toggle && this.element.contains(toggle)) {
      event.preventDefault()
      this.toggleLoadList()
      return
    }

    const row = event.target && event.target.closest
      ? event.target.closest('[data-load-index], [data-v2-load-index]')
      : null
    if (!row) return
    if (!(this.element.contains(row) || (this.formElement && this.formElement.contains(row)))) return
    if (this.selectedLoadEditorActive() && row.dataset.loadIndex === undefined) return

    const nextIndex = Number.parseInt(row.dataset.loadIndex || row.dataset.v2LoadIndex, 10)
    if (!Number.isInteger(nextIndex)) return

    if (row.dataset.loadIndex !== undefined) event.preventDefault()
    this.commitSelectedLoadEditor()
    this.selectedLoadIndex = nextIndex
    this.hydrateSelectedLoadEditor()
    this.renderLoadPreview()
  }

  toggleLoadList() {
    const rows = this.loadRowsForRender()
    const collapsedCount = this.collapsedLoadListCount()
    if (rows.length <= collapsedCount) return

    this.commitSelectedLoadEditor()
    this.loadListExpanded = !this.loadListExpanded
    if (!this.loadListExpanded && this.selectedLoadIndex >= collapsedCount) {
      this.selectedLoadIndex = 0
      this.hydrateSelectedLoadEditor()
      this.renderLoadPreview()
      return
    }

    this.syncLoadPreviewChrome(rows)
  }

  scheduleLoadRender() {
    if (this.renderTimer) window.cancelAnimationFrame(this.renderTimer)
    this.renderTimer = window.requestAnimationFrame(() => {
      this.renderTimer = null
      this.renderLoadPreview()
    })
  }

  renderLoadPreview() {
    const rows = this.loadRowsForRender()
    const selectedRow = rows[this.safeLoadIndex(rows.length)]
    const loadVisual = selectedRow ? this.loadVisual(selectedRow) : null

    this.syncLoadPreviewChrome(rows)
    if (this.loadPreviewState === 'unavailable') {
      this.showPlaceholder(this.loadPreviewError || 'render_unavailable')
      return
    }

    if (!this.panel) {
      this.showPlaceholder('Loading preview...', 'Load preview')
      return
    }

    if (!loadVisual) {
      this.panel.clear()
      this.panel.showStatus('Enter L, W, and H')
      return
    }

    this.panel.update({
      loadVisuals: [loadVisual],
      renderOptions: DEFAULT_RENDER_OPTIONS,
    })
  }

  loadRowsForRender() {
    const initialRows = this.editableLoadRows || this.normalizeLoadRows(this.initialLoadRows)
    const formRows = this.normalizeLoadRows(this.currentFormLoadRows())
    const hasExtraRows = this.extraLoadRowElements().length > 0
    const hasEditablePrimaryLoad = this.primaryLoadFieldsPresent()

    if (initialRows.length > 1 && !hasExtraRows) return initialRows
    if (hasExtraRows || hasEditablePrimaryLoad) return formRows
    if (initialRows.length > 1) return initialRows
    if (formRows.length > 0) return formRows
    return initialRows
  }

  currentFormLoadRows() {
    const form = this.formElement
    if (!form) return []

    const rows = [
      {
        commodity_description: this.fieldValue(form, '#quote_commodity_description'),
        commodity_length: this.fieldValue(form, '#quote_commodity_length'),
        commodity_support_length: this.fieldValue(form, '#quote_commodity_support_length'),
        commodity_support_offset: this.fieldValue(form, '#quote_commodity_support_offset'),
        commodity_width: this.fieldValue(form, '#quote_commodity_width'),
        commodity_height: this.fieldValue(form, '#quote_commodity_height'),
        commodity_weight: this.fieldValue(form, '#quote_commodity_weight'),
      },
    ]

    this.extraLoadRowElements().forEach(rowElement => {
      const row = {}
      rowElement.querySelectorAll('[data-v2-load-extra-field]').forEach(field => {
        row[field.getAttribute('data-v2-load-extra-field')] = field.value || ''
      })
      rows.push(row)
    })

    return rows
  }

  extraLoadRowsContainer() {
    return this.formElement ? this.formElement.querySelector('[data-behavior~="v2-load-dimensions-extra-rows"]') : null
  }

  extraLoadRowElements() {
    const container = this.extraLoadRowsContainer()
    return container ? Array.from(container.querySelectorAll(EXTRA_LOAD_ROW_SELECTOR)) : []
  }

  primaryLoadFieldsPresent() {
    if (!this.formElement) return false

    return [
      '#quote_commodity_length',
      '#quote_commodity_width',
      '#quote_commodity_height',
      '#quote_commodity_weight',
    ].some(selector => this.formElement.querySelector(selector))
  }

  selectedLoadEditorActive() {
    return this.selectedLoadAssignmentField() && (this.editableLoadRows || []).length > 1
  }

  selectedLoadAssignmentField() {
    return this.formElement ? this.formElement.querySelector(SELECTED_LOAD_ASSIGNMENT_SELECTOR) : null
  }

  hydrateSelectedLoadEditor() {
    if (!this.selectedLoadEditorActive()) return

    const row = this.editableLoadRows[this.safeLoadIndex(this.editableLoadRows.length)]
    if (!row) return

    this.editorHydrating = true
    this.setFieldValue('#quote_commodity_description', row.commodity_description)
    this.setFieldValue('#quote_commodity_length', row.commodity_length)
    this.setFieldValue('#quote_commodity_support_length', row.commodity_support_length)
    this.setFieldValue('#quote_commodity_support_offset', row.commodity_support_offset)
    this.setFieldValue('#quote_commodity_width', row.commodity_width)
    this.setFieldValue('#quote_commodity_height', row.commodity_height)
    this.setFieldValue('#quote_commodity_weight', row.commodity_weight)
    this.syncPrimarySupportFootprintVisibility(row)
    const supportField = this.formElement.querySelector('#quote_commodity_support_length')
    const offsetField = this.formElement.querySelector('#quote_commodity_support_offset')
    if (supportField && offsetField && stringValue(supportField.value).trim() && !stringValue(offsetField.value).trim()) {
      supportField.dispatchEvent(new Event('change', { bubbles: true }))
    }
    this.syncSelectedLoadAssignmentField(row)
    this.editorHydrating = false
  }

  commitSelectedLoadEditor() {
    if (!this.selectedLoadEditorActive()) return

    const index = this.safeLoadIndex(this.editableLoadRows.length)
    const updatedRow = {
      ...this.editableLoadRows[index],
      ...this.currentPrimaryLoadRow(),
    }
    // Server rows include parsed numeric derivatives for initial rendering.
    // Once the user edits the source strings those cached values are stale and
    // must not override the live inputs (especially when support is cleared).
    ;[
      'commodity_length_in',
      'commodity_support_length_in',
      'commodity_support_offset_in',
      'commodity_width_in',
      'commodity_height_in',
      'commodity_weight_lb',
      'weight_lb',
      'support_length_in',
      'support_offset_in',
    ].forEach(key => delete updatedRow[key])

    this.editableLoadRows[index] = updatedRow
    this.syncSelectedLoadAssignmentField(updatedRow)
  }

  currentPrimaryLoadRow() {
    const form = this.formElement
    if (!form) return {}

    return {
      commodity_description: this.fieldValue(form, '#quote_commodity_description'),
      commodity_length: this.fieldValue(form, '#quote_commodity_length'),
      commodity_support_length: this.fieldValue(form, '#quote_commodity_support_length'),
      commodity_support_offset: this.fieldValue(form, '#quote_commodity_support_offset'),
      commodity_support_geometry: this.primarySupportFootprintVisible(),
      commodity_width: this.fieldValue(form, '#quote_commodity_width'),
      commodity_height: this.fieldValue(form, '#quote_commodity_height'),
      commodity_weight: this.fieldValue(form, '#quote_commodity_weight'),
    }
  }

  setFieldValue(selector, value) {
    if (!this.formElement) return

    const field = this.formElement.querySelector(selector)
    if (field) field.value = stringValue(value)
  }

  syncSelectedLoadAssignmentField(row) {
    const field = this.selectedLoadAssignmentField()
    if (!field) return

    field.value = stringValue(row.quote_load_assignment_id || row.id)
  }

  normalizeLoadRows(rows) {
    return (rows || [])
      .map((row, index) => {
        const source = row || {}
        return {
          id: source.id || `load-${index + 1}`,
          quote_load_assignment_id: stringValue(source.quote_load_assignment_id || source.assignment_id),
          label: source.label || `Load ${index + 1}`,
          commodity_description: stringValue(source.commodity_description || source.description),
          commodity_length: stringValue(source.commodity_length),
          commodity_support_length: stringValue(source.commodity_support_length),
          commodity_support_offset: stringValue(source.commodity_support_offset),
          commodity_support_geometry: this.booleanValue(source.commodity_support_geometry),
          commodity_width: stringValue(source.commodity_width),
          commodity_height: stringValue(source.commodity_height),
          commodity_weight: stringValue(source.commodity_weight),
          weight_lb: this.numberOrNull(source.weight_lb || source.commodity_weight_lb),
          commodity_length_in: this.numberOrNull(source.commodity_length_in),
          commodity_support_length_in: this.numberOrNull(
            source.commodity_support_length_in || source.support_length_in
          ),
          commodity_support_offset_in: this.numberOrNull(
            source.commodity_support_offset_in == null
              ? source.support_offset_in
              : source.commodity_support_offset_in
          ),
          commodity_width_in: this.numberOrNull(source.commodity_width_in),
          commodity_height_in: this.numberOrNull(source.commodity_height_in),
        }
      })
      .filter(row => this.usefulLoadRow(row))
  }

  usefulLoadRow(row) {
    return [
      row.commodity_description,
      row.commodity_length,
      row.commodity_support_length,
      row.commodity_support_offset,
      row.commodity_width,
      row.commodity_height,
      row.commodity_weight,
      row.weight_lb,
      row.commodity_length_in,
      row.commodity_support_length_in,
      row.commodity_support_offset_in,
      row.commodity_width_in,
      row.commodity_height_in,
    ].some(value => value !== null && value !== undefined && String(value).trim().length > 0)
  }

  primarySupportFootprintRow() {
    return this.formElement
      ? this.formElement.querySelector('.v2-support-footprint-control[data-v2-load-index="0"]')
      : null
  }

  primarySupportFootprintVisible() {
    const row = this.primarySupportFootprintRow()
    const fields = row && row.querySelector('[data-behavior~="v2-support-footprint-fields"]')
    return Boolean(fields && !fields.classList.contains('hidden'))
  }

  syncPrimarySupportFootprintVisibility(row) {
    const primaryRow = this.primarySupportFootprintRow()
    if (!primaryRow) return

    const fields = primaryRow.querySelector('[data-behavior~="v2-support-footprint-fields"]')
    const offsetField = primaryRow.querySelector('[data-v2-load-field="commodity_support_offset"]')
    const active = this.booleanValue(row && row.commodity_support_geometry) ||
      stringValue(row && row.commodity_support_length).trim().length > 0 ||
      stringValue(row && row.commodity_support_offset).trim().length > 0
    if (fields) fields.classList.toggle('hidden', !active)
    if (offsetField) {
      offsetField.dataset.v2SupportOffsetMode = stringValue(row && row.commodity_support_offset).trim().length > 0
        ? 'manual'
        : 'auto'
    }
  }

  booleanValue(value) {
    if (value === true) return true

    const normalized = stringValue(value).trim().toLowerCase()
    return normalized === 'true' || normalized === '1'
  }

  safeLoadIndex(rowCount) {
    if (rowCount <= 0) {
      this.selectedLoadIndex = 0
      return 0
    }

    if (this.selectedLoadIndex < 0 || this.selectedLoadIndex >= rowCount) this.selectedLoadIndex = 0
    return this.selectedLoadIndex
  }

  syncLoadPreviewChrome(rows) {
    this.element.classList.toggle('is-multi-load', rows.length > 1)
    const layout = this.element.closest('.quote-v2-load-dimensions-grid')
    if (layout) layout.classList.toggle('quote-v2-load-dimensions-grid--multi', rows.length > 1)
    const collapsedCount = this.collapsedLoadListCount()
    const hasOverflowLoads = rows.length > collapsedCount
    if (!hasOverflowLoads) this.loadListExpanded = false
    this.element.classList.toggle('is-load-list-expanded', hasOverflowLoads && this.loadListExpanded)

    this.loadRowTargets.forEach(row => {
      const rowIndex = Number.parseInt(row.dataset.loadIndex, 10)
      row.classList.toggle('is-selected', rowIndex === this.selectedLoadIndex)
    })

    this.loadListToggleTargets.forEach(button => {
      const expanded = hasOverflowLoads && this.loadListExpanded
      button.hidden = !hasOverflowLoads
      button.setAttribute('aria-expanded', String(expanded))
      button.textContent = expanded
        ? button.dataset.expandedLabel || 'Show fewer loads'
        : button.dataset.collapsedLabel || 'Show more loads'
    })
  }

  collapsedLoadListCount() {
    const count = Number.parseInt(this.element.dataset.loadListCollapsedCount, 10)
    return Number.isInteger(count) && count > 0 ? count : 5
  }

  loadVisual(row) {
    const lengthIn = this.dimensionInches(row, 'commodity_length')
    const widthIn = this.dimensionInches(row, 'commodity_width')
    const heightIn = this.dimensionInches(row, 'commodity_height')
    if (!(lengthIn && widthIn && heightIn)) return null
    const weightLb = this.weightPounds(row)
    const enteredSupportLengthIn = this.dimensionInches(
      row,
      'commodity_support_length',
      'commodity_length'
    )
    const supportLengthIn = enteredSupportLengthIn && enteredSupportLengthIn <= lengthIn
      ? enteredSupportLengthIn
      : null
    const enteredSupportOffsetIn = this.dimensionInches(
      row,
      'commodity_support_offset',
      'commodity_support_offset',
      { allowZero: true }
    )
    const supportOffsetIn = supportLengthIn
      ? (enteredSupportOffsetIn == null ? (lengthIn - supportLengthIn) / 2 : enteredSupportOffsetIn)
      : null
    const supportGeometryValid = supportLengthIn && supportOffsetIn >= 0 &&
      supportOffsetIn + supportLengthIn <= lengthIn + 0.001
    const x0In = -lengthIn / 2

    return {
      x0_in: x0In,
      x1_in: lengthIn / 2,
      bottom_in: 0,
      top_in: heightIn,
      length_in: lengthIn,
      width_in: widthIn,
      height_in: heightIn,
      centerZ_in: 0,
      weight_lb: weightLb,
      previewOnly: false,
      ...(supportGeometryValid
        ? {
            support_x0_in: x0In + supportOffsetIn,
            support_x1_in: x0In + supportOffsetIn + supportLengthIn,
            ...(supportLengthIn < lengthIn - 0.001
              ? { body_bottom_in: 1.25 }
              : {}),
          }
        : {}),
    }
  }

  weightPounds(row) {
    const explicitValue = this.numberOrNull(row.weight_lb)
    if (explicitValue > 0) return explicitValue

    return parseLiveWeightPounds(row.commodity_weight)
  }

  dimensionInches(row, key, parserKey = key, options = {}) {
    const explicitValue = this.numberOrNull(row[`${key}_in`])
    if (explicitValue > 0 || (options.allowZero && explicitValue === 0)) return explicitValue

    const parsedValue = parseLiveDimensionInches(row[key], parserKey, options)
    if (parsedValue > 0 || (options.allowZero && parsedValue === 0)) return parsedValue

    return null
  }

  fieldValue(root, selector) {
    const field = root.querySelector(selector)
    return field ? field.value || '' : ''
  }

  numberOrNull(value) {
    if (value === null || value === undefined || value === '') return null

    const number = Number(value)
    return Number.isFinite(number) ? number : null
  }

  bindFormStretchControls() {
    if (!this.showStretchControls) return

    this.formElement = this.element.closest('form')
    if (!this.formElement) return

    this.handleFormStretchControlChange = event => {
      const target = event.target
      if (!(target && target.matches)) return
      if (!target.matches('[data-behavior~="stretch-enabled"], [data-behavior~="unit-type-select"]')) return

      window.setTimeout(() => this.syncStretchControlsWithForm(), 0)
    }

    this.formElement.addEventListener('change', this.handleFormStretchControlChange)
  }

  bindRenderStretchControls() {
    if (!this.hasStretchControlTarget) return

    this.handleRenderStretchControlClick = event => {
      const button = event.target && event.target.closest
        ? event.target.closest('[data-stretch-mode]')
        : null
      if (!(button && this.stretchControlTarget.contains(button))) return

      event.preventDefault()
      this.setStretchMode(button.dataset.stretchMode)
    }

    this.stretchControlTarget.addEventListener('click', this.handleRenderStretchControlClick)
  }

  unbindRenderStretchControls() {
    if (!(this.hasStretchControlTarget && this.handleRenderStretchControlClick)) return

    this.stretchControlTarget.removeEventListener('click', this.handleRenderStretchControlClick)
    this.handleRenderStretchControlClick = null
  }

  unbindFormStretchControls() {
    if (!(this.formElement && this.handleFormStretchControlChange)) return

    this.formElement.removeEventListener('change', this.handleFormStretchControlChange)
    this.formElement = null
    this.handleFormStretchControlChange = null
  }

  syncStretchControlsWithForm() {
    const enabled = this.stretchControlsActive && this.renderSceneStretchCapable

    if (!enabled) {
      this.currentStretchMode = 'closed'
    }

    if (this.panel && typeof this.panel.setStretchControlsEnabled === 'function') {
      this.panel.setStretchControlsEnabled(enabled)
    }

    this.syncStretchControlDisplay(enabled)
  }

  setStretchMode(stretchMode) {
    if (!this.stretchControlsActive) return

    this.currentStretchMode = stretchMode === 'extended' ? 'extended' : 'closed'

    if (this.panel && typeof this.panel.setStretchMode === 'function') {
      this.panel.setStretchMode(this.currentStretchMode)
    }

    this.syncStretchControlDisplay(this.renderSceneStretchCapable)
  }

  syncStretchControlDisplay(enabled) {
    if (!this.hasStretchControlTarget) return

    this.stretchControlTarget.hidden = !(this.showStretchControls && enabled)

    const buttons = this.stretchControlTarget.querySelectorAll('[data-stretch-mode]')
    buttons.forEach(button => {
      const active = button.dataset.stretchMode === this.currentStretchMode
      button.classList.toggle('is-active', active)
      button.setAttribute('aria-pressed', active ? 'true' : 'false')
    })

    if (this.hasStretchMetaTarget) {
      const extraIn = this.renderSceneStretchExtraIn
      this.stretchMetaTarget.textContent = extraIn > 0 ? `+${this.formatFeetInches(extraIn)}` : ''
    }
  }

  formatFeetInches(valueIn) {
    return this.trailer3d && typeof this.trailer3d.formatFeetInches === 'function'
      ? this.trailer3d.formatFeetInches(valueIn)
      : ''
  }

  get renderPayload() {
    if (this._renderPayload !== undefined) return this._renderPayload

    try {
      this._renderPayload = JSON.parse(this.payloadTarget.textContent)
    } catch (_error) {
      this._renderPayload = null
    }

    return this._renderPayload
  }

  get initialLoadRows() {
    const payload = this.renderPayload
    return payload && Array.isArray(payload.load_rows) ? payload.load_rows : []
  }

  get loadPreview() {
    return this.element.dataset.loadPreview === 'true'
  }

  get livePreviewUrl() {
    const url = stringValue(this.element.dataset.livePreviewUrl).trim()
    return url || null
  }

  get livePreviewEnabled() {
    return !this.loadPreview && this.livePreviewUrl !== null
  }

  get allowDefaultLegalEnvelope() {
    return this.element.dataset.allowDefaultLegalEnvelope === 'true'
  }

  get showStretchControls() {
    return this.element.dataset.showStretchControls === 'true'
  }

  get renderSceneStretch() {
    // Live-preview scenes supersede the initial script-tag payload; without
    // this the stretch controls keep reading the page-load scene forever.
    const scene = this.liveScene || (this.renderPayload && this.renderPayload.render_scene)
    return (scene && scene.stretch) || {}
  }

  get renderSceneStretchExtraIn() {
    const stretch = this.renderSceneStretch
    const extra = Number(stretch.max_extra_in || stretch.maxExtra_in)
    return Number.isFinite(extra) && extra > 0 ? extra : 0
  }

  get renderSceneStretchCapable() {
    const stretch = this.renderSceneStretch
    return this.renderSceneStretchExtraIn > 0 && (
      stretch.enabled === true ||
      stretch.enabled === 'true' ||
      Array.isArray(stretch.stretch_surface_ids) ||
      Array.isArray(stretch.stretchSurfaceIds)
    )
  }

  get stretchControlsActive() {
    if (!this.showStretchControls) return false

    const form = this.formElement || this.element.closest('form')
    if (!form) return true

    return Array.from(form.querySelectorAll('[data-behavior~="stretch-enabled"]')).some(input => {
      const wrapper = input.closest('[data-geometry-stretch-field]')
      return input.checked && !(wrapper && wrapper.classList.contains('hidden'))
    })
  }
}
